import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  withHandlers,
  lifecycle
} from 'recompose';
import { withRouter } from 'react-router';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import SendCodeButton from '../../components/atoms/SendCodeButton';
import BackButton from '../../components/atoms/BackButton';
import RegisterButton from '../../components/atoms/RegisterButton';
import RequiredButton from '../../components/atoms/RequiredButton';
import ProgressDialog from '../../components/molecules/ProgressDialog';
import { getShops } from '../../actions/Shop';
import { editDepositRegister } from '../../actions/Deposits';
import { getAccounts } from '../../actions/Accounts';
import {
  getCurrentUser,
  setPreferredMFA,
  updatePhone,
  verifyPhone,
  verifyPhoneSubmit,
  sendLogin
} from '../../auth/Auth';

const styles = {
  root: {
    position: 'absolute',
    width: '85%',
    height: 'auto',
    maxHeight: '80%',
    top: '5%',
    left: '2%',
    backgroundColor: '#fff',
    padding: 28,
    overflow: 'scroll',
    outline: 'none'
  },
  mainPaper: {
    position: 'relative',
    margin: '1px 0px 10px',
    padding: '10px 26px 10px 26px'
  },
  pageTitle: {
    margin: '20px 0px 0px',
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  description: {
    color: '#707070',
    margin: '20px 0px 0px'
  },
  subTitle: {
    margin: '20px 0px 0px',
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  divider: {
    margin: '20px 0px',
    height: '4px'
  },
  detailTable: {
    width: 'auto'
  },
  tableCellItem: {
    border: '2px solid #ffffff',
    backgroundColor: '#e0e0e0'
  },
  tableCellData: {
    border: '2px solid #ffffff'
  },
  tableCell90: {
    width: '90%'
  },
  textField100: {
    padding: '0px 5px 0px 5px',
    width: '100px'
  },
  textField: {
    padding: '0px 5px 0px 5px'
  },
  textFieldLong: {
    padding: '0px 5px 0px 5px',
    width: '500px'
  },
  editTable: {
    width: 'auto'
  },
  tableCell: {
    border: '2px solid #ffffff'
  },
  itemDiv: {
    margin: '20px auto'
  },
  caution: {
    color: 'red',
    fontSize: '1.1rem'
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      shops: state.shops.shops
    }),
    dispatch => ({
      actions: bindActionCreators(
        {
          getShops,
          editDepositRegister,
          getAccounts
        },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('openEditProgress', 'updateOpenEditProgress', false),
  withState('loading', 'updateLoading', false),
  withState('isEditSuccess', 'updateIsEditSuccess', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('phone', 'setPhone', ''),
  withState('phoneAuthCode', 'setPhoneAuthCode', ''),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    }
  }),
  lifecycle({
    componentDidMount() {}
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    backAction,
    editConfirm,
    editParam,
    enableShopBank,
    enableSmsAuthentication,
    updateOpenEditModal,
    updateOpenEditConfirm,
    openEditProgress,
    updateOpenEditProgress,
    loading,
    updateLoading,
    isEditSuccess,
    updateIsEditSuccess,
    resultMessage,
    setResultMessage,
    phone,
    setPhone,
    phoneAuthCode,
    setPhoneAuthCode
  }) => {
    const sendCodeAction = async () => {
      updateLoading(true);
      updateOpenEditProgress(true);
      getCurrentUser()
        .then(async user => {
          const phoneWithCountry = `+81${phone.replace(/-/g, '').slice(1)}`;
          await setPreferredMFA(user, 'NOMFA');
          await updatePhone(user, phoneWithCountry);
          await verifyPhone(user);
          setResultMessage('認証コードを送信しました。');
          updateLoading(false);
        })
        .catch(() => {
          setResultMessage('システムエラーが発生しました。');
          updateLoading(false);
        });
    };

    const editDepositRegisterAction = async () => {
      let result;
      if (enableShopBank) {
        const editedDepositRegister = await actions
          .editDepositRegister(editConfirm.shopId, editParam)
          .then(data => checkSession(data));
        await actions.getAccounts({ cognitoUserId: '' });
        result = editedDepositRegister.type === 'EDIT_DEPOSIT_REGISTER_SUCCESS';
      } else {
        result = true;
      }
      if (result) {
        updateIsEditSuccess(true);
        actions.getShops();
        setResultMessage('編集が完了しました。');
        updateLoading(false);
      } else {
        updateIsEditSuccess(false);
        setResultMessage('編集に失敗しました。');
        updateLoading(false);
      }
    };

    const editAction = async () => {
      updateLoading(true);
      updateOpenEditProgress(true);
      if (enableSmsAuthentication) {
        getCurrentUser()
          .then(async user => {
            await verifyPhoneSubmit(user, phoneAuthCode);
            // await setPreferredMFA(user, 'SMS');

            editDepositRegisterAction();
          })
          .catch(() => {
            updateIsEditSuccess(false);
            setResultMessage('システムエラーが発生しました。');
            updateLoading(false);
          });
      } else {
        editDepositRegisterAction();
      }
    };

    const progressOkAction = () => {
      if (isEditSuccess) {
        updateOpenEditModal(false);
        updateOpenEditConfirm(false);
        updateOpenEditProgress(false);
      } else {
        updateOpenEditProgress(false);
      }
    };
    return (
      <div className={classes.root}>
        <Typography variant="h6">入金情報登録確認</Typography>
        <Paper className={classes.mainPaper} elevation={1}>
          <Table className={classes.detailTable}>
            {enableShopBank && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  入金管理
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.depositManageStr}
                </TableCell>
              </TableRow>
            )}
            {enableShopBank && editConfirm.depositManage === 'NONSELF' && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>銀行名</TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.bankName}
                </TableCell>
              </TableRow>
            )}
            {enableShopBank && editConfirm.depositManage === 'NONSELF' && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>支店名</TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.bankBranchName}
                </TableCell>
              </TableRow>
            )}
            {enableShopBank && editConfirm.depositManage === 'NONSELF' && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  口座種別
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.bankAccountTypeStr}
                </TableCell>
              </TableRow>
            )}
            {enableShopBank && editConfirm.depositManage === 'NONSELF' && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  口座番号
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.bankAccountNumber}
                </TableCell>
              </TableRow>
            )}
            {enableShopBank && editConfirm.depositManage === 'NONSELF' && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  口座名義
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.bankAccountHolder}
                </TableCell>
              </TableRow>
            )}
          </Table>
          {enableShopBank && enableSmsAuthentication && (
            <Divider className={classes.divider} />
          )}
          {enableSmsAuthentication && (
            <React.Fragment>
              <div className={classes.subTitle}>
                <Typography variant="h6">２段階認証</Typography>
                <Typography className={classes.description}>
                  SMS認証を行いますので、認証コードが送付可能な携帯電話番号をご入力ください。
                  <br />
                  携帯電話番号入力後、認証コードを送信し、SMSで届いた認証コードをご入力ください。
                </Typography>
              </div>
              <Table className={classes.editTable}>
                <TableRow>
                  <TableCell className={classes.tableCell}>
                    <RequiredButton />
                  </TableCell>
                  <TableCell
                    className={classNames(
                      classes.tableCell,
                      classes.tableCell90
                    )}
                  >
                    <TextField
                      required
                      label="電話番号"
                      fullWidth
                      className={classes.textField}
                      margin="normal"
                      onChange={e => {
                        setPhone(e.target.value);
                      }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.tableCell} />
                  <TableCell className={classes.tableCell}>
                    <SendCodeButton onClickAction={sendCodeAction} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.tableCell} />
                  <TableCell
                    className={classNames(classes.tableCell, classes.caution)}
                  >
                    SMS認証コードが届くまでに時間がかかる場合があります。
                    SMSの認証コード送信ボタンを２回押すと必ずエラーとなり進めなくなりますので、そのままお待ちください。
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.tableCell}>
                    <RequiredButton />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <TextField
                      required
                      label="認証コード"
                      fullWidth
                      className={classes.textField}
                      margin="normal"
                      onChange={e => {
                        setPhoneAuthCode(e.target.value);
                      }}
                    />
                  </TableCell>
                </TableRow>
              </Table>
            </React.Fragment>
          )}
        </Paper>
        <div>
          <BackButton onClickAction={backAction} />
          <RegisterButton onClickAction={editAction} />
        </div>
        <ProgressDialog
          openProgress={openEditProgress}
          progressOkAction={progressOkAction}
          loading={loading}
          resultMessage={resultMessage}
        />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
